import { fetcher } from '../../lib/api-fetcher';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AccountNode,
  JournalEntry,
  CreateJournalPayload,
  AccountingPeriod,
  PreCloseCheckResult
} from './schemas';


function unwrapData<T>(res: unknown): T {
  if (res && typeof res === 'object' && 'data' in res) {
    return (res as { data: T }).data;
  }
  return res as T;
}

export const accountsClient = {
  prefix: '/staff/v1/accounts',
};

export function useAccountsTree() {
  return useQuery({
    queryKey: ['accounts', 'tree'],
    queryFn: async (): Promise<AccountNode[]> => {
      const res = await fetcher.get<AccountNode[] | { data: AccountNode[] }>('/staff/v1/accounts');
      return unwrapData<AccountNode[]>(res) || [];
    }
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => fetcher.post('/staff/v1/accounts', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', 'tree'] });
    }
  });
}

export function useUpdateAccount(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => fetcher.patch(`/staff/v1/accounts/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', 'tree'] });
    }
  });
}

export function useDeactivateAccount(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => fetcher.post(`/staff/v1/accounts/${id}/deactivate`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', 'tree'] });
    }
  });
}

export function useJournalEntries(filters?: any) {
  return useQuery({
    queryKey: ['journalEntries', filters],
    queryFn: async (): Promise<JournalEntry[]> => {
      const params = filters ? '?' + new URLSearchParams(filters as Record<string, string>).toString() : '';
      const res = await fetcher.get<JournalEntry[] | { data: JournalEntry[] }>(`/staff/v1/journal-entries${params}`);
      return unwrapData<JournalEntry[]>(res) || [];
    }
  });
}

export function useJournalEntry(id: string) {
  return useQuery({
    queryKey: ['journalEntry', id],
    queryFn: async (): Promise<JournalEntry> => {
      const res = await fetcher.get<JournalEntry | { data: JournalEntry }>(`/staff/v1/journal-entries/${id}`);
      return unwrapData<JournalEntry>(res);
    },
    enabled: !!id
  });
}

export function useCreateJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateJournalPayload) => fetcher.post('/staff/v1/journal-entries', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journalEntries'] });
    }
  });
}

export function useApproveJournalEntry(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => fetcher.post(`/staff/v1/journal-entries/${id}/post`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journalEntries'] });
      queryClient.invalidateQueries({ queryKey: ['journalEntry', id] });
    }
  });
}

export interface GeneralLedgerLine {
  entryId: string;
  entryNumber: string;
  entryDate: string;
  description: string;
  drAmount: number;
  crAmount: number;
  runningBalance: number;
}

export interface GeneralLedgerResponse {
  data: GeneralLedgerLine[];
  meta: {
    openingBalance: number;
    closingBalance: number;
  };
}

export interface CommissionLedgerEntry {
  id: string;
  accountId: string;
  date: string;
  debitPaise: number;
  creditPaise: number;
  balancePaise: number;
  narration: string;
  sourceType: string;
  sourceId: string;
}

export interface CommissionLedgerResponse {
  fromDate: string;
  toDate: string;
  totalCommissionPaise: number;
  entries: CommissionLedgerEntry[];
}

export function useGeneralLedger(accountId: string, filters?: any) {
  return useQuery({
    queryKey: ['ledger', 'general', accountId, filters],
    queryFn: async (): Promise<GeneralLedgerResponse> => {
      const params = filters ? '?' + new URLSearchParams(filters as Record<string, string>).toString() : '';
      const res = await fetcher.get<GeneralLedgerResponse>(`/staff/v1/ledger/${accountId}${params}`);
      return res;
    },
    enabled: !!accountId
  });
}

export function useCommissionLedger(filters?: any) {
  return useQuery({
    queryKey: ['ledger', 'commission', filters],
    queryFn: async (): Promise<CommissionLedgerResponse> => {
      const params = filters ? '?' + new URLSearchParams(filters as Record<string, string>).toString() : '';
      const res = await fetcher.get<CommissionLedgerResponse | { data: CommissionLedgerResponse }>(`/staff/v1/commissions/ledger${params}`);
      return unwrapData<CommissionLedgerResponse>(res);
    }
  });
}

export function useAccountingPeriods() {
  return useQuery({
    queryKey: ['periods'],
    queryFn: async (): Promise<AccountingPeriod[]> => {
      const res = await fetcher.get<AccountingPeriod[] | { data: AccountingPeriod[] }>('/staff/v1/periods');
      return unwrapData<AccountingPeriod[]>(res) || [];
    }
  });
}

export function usePeriodPreCloseCheck(periodId: string) {
  return useQuery({
    queryKey: ['periods', 'precheck', periodId],
    queryFn: async (): Promise<PreCloseCheckResult> => {
      const res = await fetcher.get<PreCloseCheckResult | { data: PreCloseCheckResult }>(`/staff/v1/periods/${periodId}/preclose-check`);
      return unwrapData<PreCloseCheckResult>(res);
    },
    enabled: !!periodId
  });
}


export function useCloseAccountingPeriod(periodId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => fetcher.post(`/staff/v1/periods/${periodId}/close`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['periods'] });
    }
  });
}

export function useCreateAccountingPeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { startDate: string; endDate: string }) =>
      fetcher.post('/staff/v1/periods', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['periods'] });
    }
  });
}